import { Box, Dialog } from '@mui/material';
import { fibeTokens } from '../../../theme';

function FibeDialogIcon({ tone, children }) {
    const palette = fibeTokens.dialog.icon[tone] ?? fibeTokens.dialog.icon.red;
    return (
        <Box
            sx={{
                width: 36,
                height: 36,
                flex: 'none',
                borderRadius: '10px',
                background: palette.background,
                border: `1px solid ${palette.border}`,
                color: palette.color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            {children}
        </Box>
    );
}

export function fieldLabelSx() {
    return { display: 'block', fontFamily: fibeTokens.font.family, ...fibeTokens.font.fieldLabel, marginBottom: '6px' };
}

function AlertGlyph({ variant }) {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" style={{ flex: 'none', marginTop: 1 }}>
            <circle cx="12" cy="12" r="9" />
            {variant === 'info' ? <path d="M12 16v-5M12 8h.01" /> : <path d="M12 8v5M12 16h.01" />}
        </svg>
    );
}

const DIALOG_ALERT_TONES = {
    info: { background: fibeTokens.color.infoTint, border: fibeTokens.color.info, text: fibeTokens.color.info, glyph: 'info' },
    warning: { background: fibeTokens.color.warningTint, border: fibeTokens.color.warning, text: fibeTokens.color.warning, glyph: 'caution' },
    danger: { background: fibeTokens.color.dangerTint, border: fibeTokens.color.dangerBorder, text: fibeTokens.color.danger, glyph: 'caution' },
};

export function DialogAlert({ tone, children }) {
    const palette = DIALOG_ALERT_TONES[tone];
    return (
        <Box
            sx={{
                display: 'flex',
                gap: '10px',
                padding: '12px 14px',
                background: palette.background,
                border: `1px solid ${palette.border}`,
                borderRadius: '10px',
                color: palette.text,
            }}
        >
            <AlertGlyph variant={palette.glyph} />
            <Box sx={{ fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12.5, lineHeight: 1.5 }}>{children}</Box>
        </Box>
    );
}

export function FibeDialog({ open, onClose, wide = false, tone, icon, title, subtitle, footer, children }) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            PaperProps={{
                sx: {
                    width: wide ? 580 : 470,
                    maxWidth: '92vw',
                    margin: '16px',
                    borderRadius: `${fibeTokens.density.cardRadius}px`,
                    border: `1px solid ${fibeTokens.color.border}`,
                    boxShadow: fibeTokens.dialog.shadow,
                },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '13px', padding: '20px 22px 14px' }}>
                <FibeDialogIcon tone={tone}>{icon}</FibeDialogIcon>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box
                        component="h2"
                        sx={{ margin: 0, fontFamily: fibeTokens.font.family, fontWeight: 600, fontSize: 15.5, color: fibeTokens.color.textPrimary }}
                    >
                        {title}
                    </Box>
                    {subtitle && (
                        <Box
                            component="p"
                            sx={{ margin: '4px 0 0', fontFamily: fibeTokens.font.family, fontWeight: 400, fontSize: 12.5, color: fibeTokens.color.textMuted }}
                        >
                            {subtitle}
                        </Box>
                    )}
                </Box>
            </Box>

            <Box sx={{ padding: '0 22px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {children}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    gap: '9px',
                    justifyContent: 'flex-end',
                    padding: '16px 22px',
                    marginTop: '16px',
                    borderTop: `1px solid ${fibeTokens.dialog.footerBorder}`,
                    background: fibeTokens.dialog.footerBg,
                }}
            >
                {footer}
            </Box>
        </Dialog>
    );
}

export function FibeSegmentedToggle({ value, onChange, options }) {
    return (
        <Box sx={{ display: 'flex', gap: '2px', padding: '3px', background: fibeTokens.color.trackBg, borderRadius: '9px' }}>
            {options.map((option) => {
                const isSelected = option.value === value;
                return (
                    <Box
                        key={option.value}
                        component="button"
                        type="button"
                        onClick={() => onChange(option.value)}
                        sx={{
                            flex: 1,
                            height: 30,
                            border: 'none',
                            borderRadius: '7px',
                            cursor: 'pointer',
                            fontFamily: fibeTokens.font.family,
                            fontSize: 12.5,
                            fontWeight: isSelected ? 600 : 500,
                            background: isSelected ? fibeTokens.color.surface : 'transparent',
                            color: isSelected ? fibeTokens.color.primary : fibeTokens.color.textMuted,
                            boxShadow: isSelected ? '0 1px 2px rgba(16,24,40,.1)' : 'none',
                            transition: 'background-color .14s ease, color .14s ease, box-shadow .14s ease',
                        }}
                    >
                        {option.label}
                    </Box>
                );
            })}
        </Box>
    );
}
